import _reduce from 'lodash/reduce';
import _isObject from 'lodash/isObject';
import _values from 'lodash/values';
import { getConditionalPropertyValue } from '../../../utils/conditionalValues';
import type { ConditionalValueFilters } from '../../../utils/conditionalValues';
import type { CustomStyleValue } from './styles/types';
import { getCustomStyleValue } from './styles/getCustomStyles';
import type { getStyleItemConfig } from './styles/utils';
import type { AppearanceStyles, StyleAccumulator, StyleValue, StyleResult } from './types';

function getUpdatedAppearanceStyleFromConditionValues({
  appearanceStyles,
  conditionalValueFilters,
}: {
  appearanceStyles: StyleAccumulator;
  conditionalValueFilters:
    | ConditionalValueFilters<string>
    | Record<string, ConditionalValueFilters<string>>;
}) {
  const conditionalFilter =
    'conditionalValueFilters' in conditionalValueFilters
      ? (conditionalValueFilters as ConditionalValueFilters<string>)
      : _values(conditionalValueFilters)[0];

  const value = getConditionalPropertyValue<string>({ value: conditionalFilter });

  return {
    ...appearanceStyles,
    className: value ? `${appearanceStyles.className} ${value}` : appearanceStyles.className,
  };
}

const processConditionalValue = (
  acc: StyleAccumulator,
  val: Record<string, ConditionalValueFilters<string>> | ConditionalValueFilters<string>,
): StyleAccumulator => {
  return getUpdatedAppearanceStyleFromConditionValues({
    appearanceStyles: acc,
    conditionalValueFilters: val,
  });
};

const processCustomStyle = (
  acc: StyleAccumulator,
  styleValue: string | CustomStyleValue[],
  key: string,
  styleItemsConfig?: ReturnType<typeof getStyleItemConfig>,
): StyleAccumulator => {
  return getCustomStyleValue({
    styleValue,
    key,
    initialStylesAndClassName: acc,
    styleItemsConfig,
  });
};

const processRegularStyle = (
  acc: StyleAccumulator,
  styleString: string | number,
): StyleAccumulator => {
  if (styleString === 'none') {
    return acc;
  }

  return {
    className: `${acc.className} ${styleString as string}`,
    style: acc.style,
  };
};

// determines if a value contains conditional filters
const hasConditionalFilters = (val: StyleValue): boolean => {
  if (!_isObject(val)) return false;

  const hasDirectConditionalFilters = 'conditionalValueFilters' in val;
  const firstValue = _values(val)[0];
  const hasNestedConditionalFilters =
    _isObject(firstValue) && 'conditionalValueFilters' in firstValue;

  return Boolean(hasDirectConditionalFilters || hasNestedConditionalFilters);
};

export const getBlockAppearanceAndStyles = ({
  appearanceStyles,
  styleItemsConfig,
}: {
  appearanceStyles: AppearanceStyles;
  styleItemsConfig?: ReturnType<typeof getStyleItemConfig>;
}): StyleResult => {
  // early return for empty styles
  if (!appearanceStyles) {
    return { className: '' };
  }

  const initialAcc: StyleAccumulator = { className: '', style: {} };

  // process each style entry and build up the result
  const result = _reduce(
    appearanceStyles,
    (acc, val: StyleValue, key) => {
      if (!val) return acc;

      // handle conditional values
      if (hasConditionalFilters(val)) {
        return processConditionalValue(
          acc,
          val as Record<string, ConditionalValueFilters<string>> | ConditionalValueFilters<string>,
        );
      }

      // extract style information
      const styleType = Object.keys(val)[0];
      const styleValue = typeof val === 'object' ? _values(val) : val;

      // handle custom styles
      if (styleType === 'custom') {
        return processCustomStyle(
          acc,
          styleValue as string | CustomStyleValue[],
          key,
          styleItemsConfig,
        );
      }

      // handle regular styles
      const styleString = Array.isArray(styleValue) ? styleValue[0] : styleValue;
      if (typeof styleString === 'undefined') return acc;

      // ensure we only pass string or number to processRegularStyle
      if (typeof styleString === 'string' || typeof styleString === 'number') {
        return processRegularStyle(acc, styleString);
      }

      return acc;
    },
    initialAcc,
  );

  return {
    className: result.className.trim(),
    style: result.style,
  };
};
